import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CaretLeft, CaretRight, MapPin, Users, Coins } from "@phosphor-icons/react";
import { formatCHF } from "../lib/api";
import { disciplineLabel } from "../lib/disciplines";

const DAY_MS = 24 * 60 * 60 * 1000;

// Monday-start week
function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1 - day);
  x.setDate(x.getDate() + diff);
  return x;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmtDayShort(d, locale) {
  return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "2-digit" });
}
function fmtDayLong(d, locale) {
  return d.toLocaleDateString(locale, { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}
function fmtTime(d, locale) {
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}
function fmtRange(a, b, locale) {
  return `${a.toLocaleDateString(locale, { day: "2-digit", month: "short" })} — ${b.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" })}`;
}

function CourseTile({ course, lang, t, compact = false }) {
  const start = new Date(course.starts_at);
  const seatsLeft = course.capacity - (course.bookings_count || 0);
  const sold = seatsLeft <= 0;
  return (
    <Link
      to={`/courses/${course.id}`}
      className={`block border border-white/10 bg-black hover:border-[#E30613]/60 transition-colors ${compact ? "p-2" : "p-3"}`}
      data-testid={`calendar-course-${course.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`font-mono font-bold text-[#E30613] ${compact ? "text-xs" : "text-sm"}`}>
          {fmtTime(start, lang)}
        </span>
        {sold ? (
          <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{t("common.complete")}</span>
        ) : (
          <span className="text-[9px] uppercase tracking-widest text-[#00FF66] font-bold">{seatsLeft} {t("common.seats")}</span>
        )}
      </div>
      <div className={`text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold mt-1 truncate`}>
        {disciplineLabel(course.discipline, lang)}
      </div>
      <div className={`font-display uppercase font-black text-white leading-tight line-clamp-2 mt-1 ${compact ? "text-sm" : "text-base"}`}>
        {course.title}
      </div>
      {!compact && (
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-zinc-400">
          <span className="flex items-center gap-1"><MapPin size={11} className="text-[#E30613]" />{course.location}</span>
          <span className="flex items-center gap-1"><Users size={11} className="text-[#E30613]" />{course.capacity}</span>
          <span className="flex items-center gap-1 text-white font-bold"><Coins size={11} className="text-[#E30613]" />{formatCHF(course.price_chf)}</span>
        </div>
      )}
      {compact && (
        <div className="mt-1 text-[10px] text-zinc-500 truncate">{course.instructor_name}</div>
      )}
    </Link>
  );
}

export function WeekView({ courses, lang, t }) {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const weekEnd = useMemo(() => new Date(anchor.getTime() + 7 * DAY_MS - 1), [anchor]);
  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => new Date(anchor.getTime() + i * DAY_MS));
  }, [anchor]);

  const byDay = useMemo(() => {
    const map = new Map();
    days.forEach((d) => map.set(startOfDay(d).getTime(), []));
    courses.forEach((c) => {
      const dt = new Date(c.starts_at);
      const key = startOfDay(dt).getTime();
      if (map.has(key)) map.get(key).push(c);
    });
    // sort each day
    map.forEach((arr) => arr.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
    return map;
  }, [courses, days]);

  const shift = (weeks) => setAnchor((d) => new Date(d.getTime() + weeks * 7 * DAY_MS));
  const today = () => setAnchor(startOfWeek(new Date()));

  return (
    <div data-testid="agenda-week-view">
      <div className="flex items-center justify-between border border-white/10 bg-[#0A0A0A] px-4 py-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="w-9 h-9 border border-white/20 hover:border-[#E30613] hover:text-[#E30613] text-zinc-300 flex items-center justify-center" data-testid="week-prev-btn" aria-label="Previous week">
            <CaretLeft size={16} weight="bold" />
          </button>
          <button onClick={today} className="h-9 px-3 border border-white/20 hover:border-[#E30613] hover:text-[#E30613] text-zinc-300 text-[10px] uppercase tracking-widest font-bold" data-testid="week-today-btn">
            {t("agenda.today")}
          </button>
          <button onClick={() => shift(1)} className="w-9 h-9 border border-white/20 hover:border-[#E30613] hover:text-[#E30613] text-zinc-300 flex items-center justify-center" data-testid="week-next-btn" aria-label="Next week">
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
        <div className="font-display text-sm sm:text-base uppercase font-black text-white tracking-tight" data-testid="week-range-label">
          {fmtRange(anchor, weekEnd, lang)}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2 mt-3">
        {days.map((d) => {
          const list = byDay.get(startOfDay(d).getTime()) || [];
          const isToday = sameDay(d, new Date());
          return (
            <div key={d.toISOString()} className={`border ${isToday ? "border-[#E30613]/60" : "border-white/10"} bg-[#0A0A0A] p-2 min-h-[220px]`} data-testid={`week-day-${d.toISOString().slice(0,10)}`}>
              <div className={`text-[10px] uppercase tracking-[0.2em] font-bold pb-2 border-b mb-2 ${isToday ? "text-[#E30613] border-[#E30613]/40" : "text-zinc-500 border-white/10"}`}>
                {fmtDayShort(d, lang)}
              </div>
              {list.length === 0 ? (
                <div className="text-[10px] text-zinc-600 uppercase tracking-widest text-center py-4">—</div>
              ) : (
                <div className="space-y-2">
                  {list.map((c) => <CourseTile key={c.id} course={c} lang={lang} t={t} compact />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DayView({ courses, lang, t }) {
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const list = useMemo(() => {
    return courses
      .filter((c) => sameDay(new Date(c.starts_at), anchor))
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  }, [courses, anchor]);

  const shift = (d) => setAnchor((x) => new Date(x.getTime() + d * DAY_MS));
  const today = () => setAnchor(startOfDay(new Date()));

  return (
    <div data-testid="agenda-day-view">
      <div className="flex items-center justify-between border border-white/10 bg-[#0A0A0A] px-4 py-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="w-9 h-9 border border-white/20 hover:border-[#E30613] hover:text-[#E30613] text-zinc-300 flex items-center justify-center" data-testid="day-prev-btn" aria-label="Previous day">
            <CaretLeft size={16} weight="bold" />
          </button>
          <button onClick={today} className="h-9 px-3 border border-white/20 hover:border-[#E30613] hover:text-[#E30613] text-zinc-300 text-[10px] uppercase tracking-widest font-bold" data-testid="day-today-btn">
            {t("agenda.today")}
          </button>
          <button onClick={() => shift(1)} className="w-9 h-9 border border-white/20 hover:border-[#E30613] hover:text-[#E30613] text-zinc-300 flex items-center justify-center" data-testid="day-next-btn" aria-label="Next day">
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
        <div className="font-display text-sm sm:text-base uppercase font-black text-white tracking-tight" data-testid="day-label">
          {fmtDayLong(anchor, lang)}
        </div>
      </div>

      <div className="mt-3">
        {list.length === 0 ? (
          <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400 text-xs uppercase tracking-widest" data-testid="day-empty">
            {t("agenda.no_courses_day")}
          </div>
        ) : (
          <div className="space-y-2" data-testid="day-list">
            {list.map((c) => <CourseTile key={c.id} course={c} lang={lang} t={t} />)}
          </div>
        )}
      </div>
    </div>
  );
}
